import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { LegalLayout, LegalSection, LegalList, LegalSubhead } from "../components/LegalLayout";

export const TermsConditions = () => (
  <LegalLayout overline="Legal · BREELINK" title="Terms & Conditions" titleTestId="terms-title">
    <p className="mt-6 text-xs font-medium uppercase tracking-[0.18em] text-inkMuted">
      Last updated: 01/01/2026
    </p>
    <p className="mt-6 text-base leading-relaxed text-inkMuted">
      Welcome to Breelnk.click, operated by ACH MEDIA INC. These Terms and Conditions govern your
      access to and use of the Breelnk.click website and related services. By accessing or using the
      website, you agree to be bound by these Terms. If you do not agree to these Terms, you must not
      use the website or services.
    </p>

    <LegalSection title="Definitions" testid="terms-section-definitions">
      <LegalList
        items={[
          "“Website” refers to Breelnk.click.",
          "“Company” refers to ACH MEDIA INC, the owner and operator of Breelnk.",
          "“User” refers to any individual or entity accessing or using the Website.",
          "“Services” refers to the software, tools, subscriptions, and features provided through Breelnk as a Software-as-a-Service (SaaS) platform.",
        ]}
      />
    </LegalSection>

    <LegalSection title="Eligibility" testid="terms-section-eligibility">
      <p>
        You must be at least 18 years old or have legal parental or guardian consent to use the
        Website and Services. By using Breelnk, you represent and warrant that you meet this
        requirement.
      </p>
    </LegalSection>

    <LegalSection title="Use of Services" testid="terms-section-use">
      <LegalSubhead>Account Registration</LegalSubhead>
      <p>
        Certain features require account creation. You agree to provide accurate, current, and
        complete information and to keep your account details up to date.
      </p>
      <LegalSubhead>User Responsibilities</LegalSubhead>
      <p>
        You are responsible for maintaining the confidentiality of your account credentials and for
        all activity occurring under your account. You agree to use the Website and Services only for
        lawful purposes and in compliance with applicable laws.
      </p>
      <LegalSubhead>Prohibited Activities</LegalSubhead>
      <p>You agree not to:</p>
      <LegalList
        items={[
          "Attempt to gain unauthorized access to systems or data.",
          "Introduce malware, viruses, or harmful code.",
          "Interfere with or disrupt the operation or security of the Website or Services.",
        ]}
      />
    </LegalSection>

    <LegalSection title="Payments and Subscriptions" testid="terms-section-payments">
      <LegalSubhead>Pricing</LegalSubhead>
      <p>
        All prices for Services are displayed on the Website and may be updated from time to time.
        Price changes do not affect completed purchases.
      </p>
      <LegalSubhead>Payment Processing</LegalSubhead>
      <p>
        Payments are processed through secure third-party payment providers. By submitting payment
        information, you represent that you are authorized to use the selected payment method.
      </p>
      <LegalSubhead>Refund Policy</LegalSubhead>
      <p>
        Refunds are governed exclusively by our Refund Policy. Breelnk offers a 14-day unconditional
        refund right on all purchases, with no conditions or exceptions. The full Refund Policy is
        available at{" "}
        <Link
          to="/refund-policy"
          data-testid="terms-refund-link"
          className="font-semibold text-ink underline decoration-lime decoration-2 underline-offset-2"
        >
          Breelnk Refund Policy
        </Link>
        .
      </p>
    </LegalSection>

    <LegalSection title="Intellectual Property" testid="terms-section-ip">
      <LegalSubhead>Ownership</LegalSubhead>
      <p>
        All content, software, branding, designs, and materials available on Breelnk.click are the
        intellectual property of ACH MEDIA INC or its licensors.
      </p>
      <LegalSubhead>Limited License</LegalSubhead>
      <p>
        Users are granted a limited, non-exclusive, non-transferable license to access and use the
        Website and Services in accordance with these Terms.
      </p>
      <LegalSubhead>Restrictions</LegalSubhead>
      <p>
        You may not copy, reproduce, distribute, modify, or create derivative works without prior
        written authorization from the Company.
      </p>
    </LegalSection>

    <LegalSection title="Limitation of Liability" testid="terms-section-liability">
      <p>
        To the fullest extent permitted by law, ACH MEDIA INC shall not be liable for any indirect,
        incidental, consequential, or special damages arising from or related to your use of the
        Website or Services, including loss of data, revenue, or business opportunities.
      </p>
    </LegalSection>

    <LegalSection title="Disclaimer of Warranties" testid="terms-section-disclaimer">
      <p>
        The Website and Services are provided on an “as is” and “as available” basis. ACH MEDIA INC
        makes no warranties, express or implied, regarding the reliability, availability, accuracy, or
        suitability of the Services.
      </p>
    </LegalSection>

    <LegalSection title="Termination" testid="terms-section-termination">
      <LegalSubhead>User Termination</LegalSubhead>
      <p>You may terminate your account at any time by contacting customer support.</p>
      <LegalSubhead>Company Termination</LegalSubhead>
      <p>
        The Company reserves the right to suspend or terminate access to the Website or Services if
        you violate these Terms or engage in unlawful or harmful conduct.
      </p>
    </LegalSection>

    <LegalSection title="Governing Law and Jurisdiction" testid="terms-section-law">
      <p>
        These Terms are governed by and construed in accordance with the laws of the United States and
        the State of Wyoming. Any disputes arising under these Terms shall be subject to the exclusive
        jurisdiction of the competent courts.
      </p>
    </LegalSection>

    <LegalSection title="Changes to These Terms" testid="terms-section-changes">
      <p>
        We may update these Terms from time to time. Changes will become effective upon posting on
        this page. Continued use of the Website after changes are posted constitutes acceptance of the
        updated Terms.
      </p>
    </LegalSection>

    <div className="mt-14 rounded-3xl border border-line bg-surfaceAlt p-7 md:p-9" data-testid="terms-contact">
      <h2 className="font-display text-xl font-semibold tracking-tight text-ink md:text-2xl">
        Contact Information
      </h2>
      <p className="mt-4 text-sm leading-relaxed text-inkMuted md:text-base">
        If you have any questions regarding these Terms and Conditions, please contact us at:
      </p>
      <a
        href="mailto:support@breelnk.click"
        data-testid="terms-contact-email"
        className="group mt-5 inline-flex items-center gap-2.5 rounded-full bg-lime px-6 py-3.5 text-sm font-semibold text-limeText transition-transform hover:-translate-y-0.5"
      >
        <Mail size={16} />
        support@breelnk.click
      </a>
      <p className="mt-6 text-sm text-inkMuted">
        Signed,
        <br />
        The Breelnk Team
      </p>
    </div>
  </LegalLayout>
);
